import { useTranslate, useTranslation } from 'module/hooks/use-translation';
import SalesforceLogo from 'module/ui/assets/svgs/salesforce-logo.svg';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  Krypton,
  KryptonMenuIcon,
} from 'module/ui/components/templates/krypton';
import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import {
  faBarsStaggered,
  faBook,
  faSignal,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faEnvelope,
  faEnvelopeOpen,
  faFilterList,
} from '@fortawesome/pro-solid-svg-icons';
import { Avatar } from '@rocketlane/ui';
import { FC, PropsWithChildren, useMemo } from 'react';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const routePrefixer = (subRoute: string) =>
  `/${ROUTE_PATHS.SETTINGS}/${subRoute}`;

export const Settings: FC<PropsWithChildren> = ({ children }) => {
  const translate = useTranslate();
  const leftMenuItems = useMemo(
    () => [
      {
        menuName: translate('signals'),
        key: 'signals',
        subMenu: [
          {
            key: 'signals-configuration',
            menuName: translate('signals'),
            menuIcon: <KryptonMenuIcon icon={faSignal} color={'blue'} />,
            route: routePrefixer(
              ROUTE_PATHS.SETTINGS_SIGNAL_CONFIGURATION_LIST,
            ),
          },
          {
            key: 'signal-fields',
            menuName: translate('signal_fields'),
            menuIcon: (
              <KryptonMenuIcon icon={faBarsStaggered} color={'magenta'} />
            ),
            route: routePrefixer(ROUTE_PATHS.SETTINGS_SIGNAL_FIELDS),
          },
        ],
      },
      {
        menuName: translate('meetings'),
        key: 'meetings',
        subMenu: [
          {
            key: 'meeting-fields',
            menuName: translate('meeting_fields'),
            menuIcon: <KryptonMenuIcon icon={faCalendar} color={'blue'} />,
            route: routePrefixer(ROUTE_PATHS.SETTINGS_MEETING_FIELDS),
          },
        ],
      },
      {
        menuName: translate('email_intelligence'),
        key: 'email',
        subMenu: [
          {
            key: 'email-intelligence',
            menuName: translate('email_settings'),
            menuIcon: (
              <KryptonMenuIcon icon={faEnvelopeOpen} color={'magenta'} />
            ),
            route: routePrefixer(ROUTE_PATHS.SETTINGS_EMAIL_INTELLIGENCE),
          },
          {
            key: 'email-fields',
            menuName: translate('email_fields'),
            menuIcon: <KryptonMenuIcon icon={faEnvelope} color={'green'} />,
            route: routePrefixer(ROUTE_PATHS.SETTINGS_EMAIL_FIELDS),
          },
          {
            key: 'email-blacklist',
            menuName: translate('email_blacklist'),
            menuIcon: <KryptonMenuIcon icon={faFilterList} color={'orange'} />,
            route: routePrefixer(ROUTE_PATHS.SETTINGS_EMAIL_BLACKLIST),
          },
        ],
      },
      {
        menuName: translate('knowledge_base.page.title'),
        key: 'knowledge-base',
        subMenu: [
          {
            key: 'knowledge-base',
            menuName: translate('knowledge_base.page.title'),
            menuIcon: <KryptonMenuIcon icon={faBook} color={'green'} />,
            route: routePrefixer(ROUTE_PATHS.KNOWLEDGE_BASE),
          },
        ],
      },
      {
        menuName: 'Integrations',
        key: 'integrations',
        subMenu: [
          {
            key: 'knowledge-base',
            menuName: 'Salesforce',
            menuIcon: <Avatar src={SalesforceLogo} shape="square" size={20} />,
            route: routePrefixer(ROUTE_PATHS.SALESFORCE_INTEGRATION),
          },
        ],
      },
    ],
    [translate],
  );
  const title = useTranslation('settings');

  return (
    <Krypton
      documentTitle={title}
      title={
        <Title>
          <FontAwesomeIcon icon={faGear} size={16} />
          {title}
        </Title>
      }
      leftMenuItems={leftMenuItems}
      content={children}
    />
  );
};
